import * as React from "react";

import { headingTag, HeadingLevelProvider, useHeadingLevel, useRegisterTitle, useTitledLevel, type HeadingLevel } from "../lib/heading";
import { cn } from "../lib/utils";
import { useInPane } from "./page-layout";
import { Icon, type IconComponent } from "./icon";

/**
 * v2 Card (redesign phase 5, #455). Storybook only until implementation;
 * see docs/design-library/04-implementation-notes.md for the swap.
 *
 * Direction B: `--card` fill, 1px `--border`, radius 12, no shadow (break #57).
 * Every part pads 16, the footer too (break #58); header and footer are 48px
 * bands. `overflow-clip` rounds the corners of flush content (a table row's
 * hover or selected tint) without making the card a scroll container, so a
 * sticky element inside still sticks to the page. Tooltips and menus are
 * portalled, so nothing they show is clipped.
 *
 * Dividers (break #59): each part draws a `--divider` hairline on its top edge
 * unless it is the card's first child. So a line appears only BETWEEN two
 * parts: under a header with something below it, above a footer with something
 * above it, and between two content blocks. A header or content block on its
 * own has none.
 *
 * `density="compact"` (the matrix's "dense" card, for AI surfaces and the
 * add-in's narrow panel): padding 12, 40px bands, 14px title, 12px description
 * (break #61). The parts read it from context. Inside a Pane a Card is compact
 * unless told otherwise: the title then matches the pane's 14 band title
 * (finding #7; a page card keeps 16).
 *
 * The header's title column has a basis (256, compact 160), not `flex-1`:
 * below title + actions the actions wrap under the title instead of squeezing
 * it to a word per line (finding #1; the PageHeader rule).
 *
 * `<CardContent flush>` drops the padding so a Table or a list runs edge to
 * edge; the Table's first and last cells pad 16 and line up with the title.
 *
 * Headings (lib/heading): CardTitle sits at the context level (h2 on a page,
 * h3 in a pane; h3 on its own), and everything inside the card one deeper,
 * as long as it has a title. `headingLevel` on CardTitle wins.
 */

export type CardDensity = "default" | "compact";

const CardDensityContext = React.createContext<CardDensity>("default");
/** The card's own title level, and how its title registers (its content then sits one deeper). */
const CardLevelContext = React.createContext<{ level: HeadingLevel; register: () => () => void } | undefined>(undefined);

/** Each part: a hairline on top unless it is the first child. */
const PART = "border-divider not-first:border-t";

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Default `compact` inside a Pane, else `default`. */
  density?: CardDensity;
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(function Card(
  { density: densityProp, className, children, ...props },
  ref,
) {
  const inPane = useInPane();
  const density = densityProp ?? (inPane ? "compact" : "default");
  const level = useHeadingLevel(undefined, 3);
  const { contentLevel, register } = useTitledLevel(level);
  const cardLevel = React.useMemo(() => ({ level, register }), [level, register]);
  return (
    <CardDensityContext.Provider value={density}>
      <CardLevelContext.Provider value={cardLevel}>
        <HeadingLevelProvider level={contentLevel}>
          <div
            ref={ref}
            data-density={density}
            className={cn(
              "flex flex-col overflow-clip rounded-xl border border-border bg-card text-card-foreground",
              className,
            )}
            {...props}
          >
            {children}
          </div>
        </HeadingLevelProvider>
      </CardLevelContext.Provider>
    </CardDensityContext.Provider>
  );
});

export interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Right-aligned controls: Buttons, IconButtons, a search Input (`size="sm"`). */
  actions?: React.ReactNode;
}

const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(function CardHeader(
  { actions, className, children, ...props },
  ref,
) {
  const compact = React.useContext(CardDensityContext) === "compact";
  return (
    <div
      ref={ref}
      className={cn(
        PART,
        "flex flex-wrap items-center gap-x-3 gap-y-2 py-2",
        compact ? "min-h-10 px-3" : "min-h-12 px-4",
        className,
      )}
      {...props}
    >
      {/* `basis-40` in both densities (break #183): the actions stay on the title line while the title keeps
          160; past that they wrap onto their own row, where they shrink to the card and wrap inside it. */}
      <div className="flex min-w-0 grow basis-40 flex-col">{children}</div>
      {actions != null && <div className="flex max-w-full min-w-0 flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
});

export interface CardTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  /** Wins over the card's context level (h2 on a page, h3 in a pane, h3 on its own). */
  headingLevel?: HeadingLevel;
  /** Leading icon: 20px next to the 16px title, 16px in a compact card. */
  icon?: IconComponent;
}

const CardTitle = React.forwardRef<HTMLHeadingElement, CardTitleProps>(function CardTitle(
  { headingLevel, icon, className, children, ...props },
  ref,
) {
  const compact = React.useContext(CardDensityContext) === "compact";
  const card = React.useContext(CardLevelContext);
  const contextLevel = useHeadingLevel(undefined, 3);
  useRegisterTitle(card?.register);
  const Heading = headingTag(headingLevel ?? card?.level ?? contextLevel);
  return (
    <Heading
      ref={ref}
      className={cn(
        "font-sans text-ink",
        compact ? "text-label" : "text-heading-sm",
        icon && "flex items-center gap-2",
        className,
      )}
      {...props}
    >
      {icon && <Icon icon={icon} size={compact ? 16 : 20} />}
      {children}
    </Heading>
  );
});

const CardDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  function CardDescription({ className, ...props }, ref) {
    const compact = React.useContext(CardDensityContext) === "compact";
    return (
      <p
        ref={ref}
        className={cn("font-body text-ink-muted", compact ? "text-caption" : "text-body", className)}
        {...props}
      />
    );
  },
);

export interface CardContentProps extends React.HTMLAttributes<HTMLDivElement> {
  /** No padding: a Table or list runs edge to edge and pads itself. */
  flush?: boolean;
}

const CardContent = React.forwardRef<HTMLDivElement, CardContentProps>(function CardContent(
  { flush = false, className, ...props },
  ref,
) {
  const compact = React.useContext(CardDensityContext) === "compact";
  return (
    <div
      ref={ref}
      className={cn(PART, "flex flex-col", flush ? "p-0" : compact ? "gap-2 p-3" : "gap-3 p-4", className)}
      {...props}
    />
  );
});

/** Actions sit at the right; give leading meta text `mr-auto`. */
const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(function CardFooter(
  { className, ...props },
  ref,
) {
  const compact = React.useContext(CardDensityContext) === "compact";
  return (
    <div
      ref={ref}
      className={cn(
        PART,
        "flex items-center justify-end gap-2 py-2",
        compact ? "min-h-10 px-3" : "min-h-12 px-4",
        className,
      )}
      {...props}
    />
  );
});

export { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter };
